import RouteNameEnum from '@/router/routeNameEnum';
import useAuthStore from '@/domains/authentication/store/auth.store';
import useNavigationStore from '@/domains/common/store/navigation.store';
import i18n from '@/translations';
import RoleEnum from '@/domains/authentication/typescript/RoleEnum';
import ForgottenPasswordView from '@/views/ForgottenPasswordView.vue';
import ResetPasswordView from '@/views/ResetPasswordView.vue';
import InvitationView from '@/views/InvitationView.vue';
import { updateToken } from '@/domains/authentication/services/authenticate';
import useSaveHash from '@/domains/order/composables/useSaveHash';
import useUpdateChecker from '@/domains/common/composables/useUpdateChecker';
import { checkPodonexVersionAnnouncement } from '@/domains/common/services/checkPodonexVersionAnnouncement';
import { growthBookKey } from '@/growthbookKey';
import { createRouter, createWebHistory } from 'vue-router';
import { inject } from 'vue';

const { t } = i18n.global;

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: [
        {
            path: '/patients',
            children: [
                {
                    path: '',
                    name: RouteNameEnum.Patients,
                    component: () => import('@/views/PatientsView.vue'),
                    meta: {
                        requiresAuth: true,
                        roles: [RoleEnum.ROLE_DOCTOR],
                    },
                },
                {
                    path: 'new-patient',
                    name: RouteNameEnum.NewPatient,
                    component: () => import('@/domains/patient/views/PatientEditView.vue'),
                    meta: {
                        requiresAuth: true,
                        roles: [RoleEnum.ROLE_DOCTOR],
                    },
                },
                {
                    path: 'edit-patient/:id',
                    name: RouteNameEnum.EditPatient,
                    component: () => import('@/domains/patient/views/PatientEditView.vue'),
                    meta: {
                        requiresAuth: true,
                        roles: [RoleEnum.ROLE_DOCTOR],
                    },
                },
            ],
        },
        {
            path: '/orders',
            children: [
                {
                    path: '',
                    name: RouteNameEnum.Orders,
                    component: () => import('@/views/OrdersView.vue'),
                    meta: {
                        requiresAuth: true,
                        roles: [RoleEnum.ROLE_DOCTOR],
                    },
                },
                {
                    path: 'patient/:id',
                    name: RouteNameEnum.PatientOrders,
                    component: () => import('@/views/OrdersView.vue'),
                    meta: {
                        requiresAuth: true,
                        roles: [RoleEnum.ROLE_DOCTOR],
                    },
                },
                {
                    path: 'new-order',
                    name: RouteNameEnum.NewOrder,
                    component: () => import('@/domains/order/views/OrderEditView.vue'),
                    meta: {
                        requiresAuth: true,
                        roles: [RoleEnum.ROLE_DOCTOR],
                    },
                },
                {
                    path: 'edit-order/:id',
                    name: RouteNameEnum.EditOrder,
                    component: () => import('@/domains/order/views/OrderEditView.vue'),
                    meta: {
                        requiresAuth: true,
                        roles: [RoleEnum.ROLE_DOCTOR],
                    },
                },
            ],
        },
        {
            path: '/settings',
            name: RouteNameEnum.Settings,
            component: () => import('@/views/SettingsView.vue'),
            meta: {
                requiresAuth: true,
                roles: [RoleEnum.ROLE_DOCTOR],
            },
        },
        {
            path: '/order/:id/report',
            name: RouteNameEnum.OrderReport,
            component: () => import('@/views/OrderReportView.vue'),
            meta: {
                requiresAuth: true,
                roles: [RoleEnum.ROLE_DOCTOR],
            },
        },
        {
            path: '/order/:id/scan',
            name: RouteNameEnum.Scan,
            component: () => import('@/views/ScanView.vue'),
            meta: {
                requiresAuth: true,
                roles: [RoleEnum.ROLE_DOCTOR],
            },
        },
        {
            path: '/settings',
            name: RouteNameEnum.Settings,
            component: () => import('@/views/SettingsView.vue'),
            meta: {
                requiresAuth: true,
                roles: [RoleEnum.ROLE_DOCTOR],
            },
        },
        {
            path: '/select-offices',
            name: RouteNameEnum.SelectOffices,
            component: () => import('@/domains/authentication/views/SelectOfficeView.vue'),
            meta: {
                requiresAuth: true,
                roles: [RoleEnum.ROLE_DOCTOR],
            },
        },
        {
            path: '/admin',
            children: [
                {
                    path: 'office',
                    children: [
                        {
                            path: '',
                            name: RouteNameEnum.AdminOffice,
                            component: () => import('@/domains/admin/views/AdminOfficeView.vue'),
                            meta: {
                                requiresAuth: true,
                                roles: [RoleEnum.ROLE_ADMIN_ERGO],
                            },
                        },
                        {
                            path: 'add',
                            name: RouteNameEnum.AdminOfficeAdd,
                            component: () => import('@/domains/admin/views/AdminOfficeAddView.vue'),
                            meta: {
                                requiresAuth: true,
                                roles: [RoleEnum.ROLE_ADMIN_ERGO],
                            },
                        },
                        {
                            path: 'edit-office/:id',
                            name: RouteNameEnum.AdminOfficeEdit,
                            component: () => import('@/domains/admin/views/AdminOfficeAddView.vue'),
                            meta: {
                                requiresAuth: true,
                                roles: [RoleEnum.ROLE_ADMIN_ERGO],
                            },
                        },
                    ],
                },
                {
                    path: 'user',
                    children: [
                        {
                            path: '',
                            name: RouteNameEnum.AdminUser,
                            component: () => import('@/domains/admin/views/AdminUserView.vue'),
                            meta: {
                                requiresAuth: true,
                                roles: [RoleEnum.ROLE_ADMIN_ERGO],
                            },
                        },
                        {
                            path: 'add',
                            name: RouteNameEnum.AdminUserAdd,
                            component: () => import('@/domains/office/views/OfficeInviteView.vue'),
                            meta: {
                                requiresAuth: true,
                                roles: [RoleEnum.ROLE_ADMIN_ERGO],
                            },
                        },
                        {
                            path: 'edit-user/:id',
                            name: RouteNameEnum.AdminUserEdit,
                            component: () => import('@/domains/admin/views/AdminUserEditView.vue'),
                            meta: {
                                requiresAuth: true,
                                roles: [RoleEnum.ROLE_ADMIN_ERGO],
                            },
                        },
                        {
                            path: 'user-secret/:id',
                            name: RouteNameEnum.AdminUserSecret,
                            component: () => import('@/domains/admin/views/AdminUserSecretView.vue'),
                            meta: {
                                requiresAuth: true,
                                roles: [RoleEnum.ROLE_ADMIN_ERGO],
                            },
                        },
                        {
                            path: 'add-admin',
                            name: RouteNameEnum.AdminAdd,
                            component: () => import('@/domains/admin/views/AdminUserAdminAddView.vue'),
                            meta: {
                                requiresAuth: true,
                                roles: [RoleEnum.ROLE_ADMIN_ERGO],
                            },
                        },
                    ],
                },
                {
                    path: 'announcement',
                    children: [
                        {
                            path: '',
                            name: RouteNameEnum.AdminAnnouncement,
                            component: () => import('@/domains/admin/views/AdminAnnouncementView.vue'),
                            meta: {
                                requiresAuth: true,
                                roles: [RoleEnum.ROLE_ADMIN_ERGO],
                            },
                        },
                        {
                            path: 'add',
                            name: RouteNameEnum.AdminAnnouncementAdd,
                            component: () => import('@/domains/admin/views/AdminAnnouncementAddView.vue'),
                            meta: {
                                requiresAuth: true,
                                roles: [RoleEnum.ROLE_ADMIN_ERGO],
                            },
                        },
                        {
                            path: 'edit-announcement/:id',
                            name: RouteNameEnum.AdminAnnouncementEdit,
                            component: () => import('@/domains/admin/views/AdminAnnouncementAddView.vue'),
                            meta: {
                                requiresAuth: true,
                                roles: [RoleEnum.ROLE_ADMIN_ERGO],
                            },
                        },
                    ],
                },
                {
                    path: 'catalog',
                    children: [
                        {
                            path: '',
                            name: RouteNameEnum.AdminCatalog,
                            component: () => import('@/domains/admin/views/AdminCatalogListView.vue'),
                            meta: {
                                requiresAuth: true,
                                roles: [RoleEnum.ROLE_ADMIN_ERGO],
                            },
                        },
                        {
                            path: 'add',
                            name: RouteNameEnum.AdminCatalogAddElement,
                            component: () => import('@/domains/admin/views/AdminCatalogNewElementView.vue'),
                            meta: {
                                requiresAuth: true,
                                roles: [RoleEnum.ROLE_ADMIN_ERGO],
                            },
                        },
                        {
                            path: 'edit/:id',
                            name: RouteNameEnum.AdminCatalogEditElement,
                            component: () => import('@/domains/admin/views/AdminCatalogEditElementView.vue'),
                            meta: {
                                requiresAuth: true,
                                roles: [RoleEnum.ROLE_ADMIN_ERGO],
                            },
                        },
                    ],
                },
                {
                    path: 'orders',
                    children: [
                        {
                            path: '',
                            name: RouteNameEnum.AdminOrders,
                            component: () => import('@/domains/admin/views/AdminOrderView.vue'),
                            meta: {
                                requiresAuth: true,
                                roles: [RoleEnum.ROLE_ADMIN_ERGO],
                            },
                        },
                    ],
                },
            ],
        },
        {
            path: '/my-office',
            children: [
                {
                    path: 'users',
                    children: [
                        {
                            path: '',
                            name: RouteNameEnum.MyOfficeUser,
                            component: () => import('@/views/OfficeUsersView.vue'),
                            meta: {
                                requiresAuth: true,
                                roles: [RoleEnum.ROLE_ADMINISTRATOR_OFFICE],
                            },
                        },
                        {
                            path: 'edit/:id',
                            name: RouteNameEnum.OfficeUserEdit,
                            component: () => import('@/domains/office/views/OfficeUserEditView.vue'),
                            meta: {
                                requiresAuth: true,
                                roles: [RoleEnum.ROLE_ADMINISTRATOR_OFFICE],
                            },
                        },
                    ],
                },
                {
                    path: 'invite',
                    name: RouteNameEnum.MyOfficeInvite,
                    component: () => import('@/domains/office/views/OfficeInviteView.vue'),
                    meta: {
                        requiresAuth: true,
                        roles: [RoleEnum.ROLE_ADMINISTRATOR_OFFICE],
                    },
                },
                {
                    path: 'edit',
                    name: RouteNameEnum.MyOfficeEdit,
                    component: () => import('@/domains/office/views/OfficeEditView.vue'),
                    meta: {
                        requiresAuth: true,
                        roles: [RoleEnum.ROLE_ADMINISTRATOR_OFFICE],
                    },
                },
                {
                    path: 'sole-preset',
                    children: [
                        {
                            path: '',
                            name: RouteNameEnum.SolePreset,
                            component: () => import('@/domains/solePreset/views/SolePresetsView.vue'),
                            meta: {
                                requiresAuth: true,
                                roles: [RoleEnum.ROLE_ADMINISTRATOR_OFFICE],
                            },
                        },
                        {
                            path: 'edit/:id',
                            name: RouteNameEnum.SolePresetEdit,
                            component: () => import('@/domains/solePreset/views/SolePresetViewer.vue'),
                            meta: {
                                requiresAuth: true,
                                roles: [RoleEnum.ROLE_ADMINISTRATOR_OFFICE],
                            },
                        },
                    ],
                },
                {
                    path: 'catalog',
                    name: RouteNameEnum.MyOfficeCatalog,
                    component: () => import('@/domains/office/components/OfficeCatalogPage.vue'),
                    meta: {
                        requiresAuth: true,
                        roles: [RoleEnum.ROLE_ADMINISTRATOR_OFFICE],
                    },
                },
            ],
        },
        {
            path: '/forgotten-password',
            name: RouteNameEnum.ForgottenPassword,
            component: ForgottenPasswordView,
        },
        {
            path: '/reset-password/:token',
            name: RouteNameEnum.ResetPassword,
            component: ResetPasswordView,
        },
        // INVITATION
        {
            path: '/invite/:token',
            name: RouteNameEnum.Invite,
            component: InvitationView,
        },
        // HOME
        {
            path: '/:token([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})?',
            name: RouteNameEnum.Login,
            component: () => import('@/views/LoginView.vue'),
        },
        // FALLBACK
        {
            path: '/:pathMatch(.*)*',
            redirect: '/',
        },
    ],
});

router.afterEach((to) => {
    const growthbook = inject(growthBookKey);
    let pageTitle = 'Podofit';
    if (to.name) {
        pageTitle = t(`page_title.${to.name.toString()}`);
    }
    document.title = `${pageTitle} - Podofit Web`;
    const { checkVersion } = useUpdateChecker();
    checkVersion();
    if (growthbook?.isOn('podonex-alert')) {
        checkPodonexVersionAnnouncement();
    }
});

router.beforeEach(async (to, from, next) => {
    const growthbook = inject(growthBookKey);
    const authStore = useAuthStore();
    const navigationStore = useNavigationStore();
    const { shouldSave } = useSaveHash();
    if (authStore.isLoadingUser) {
        await updateToken(to);
    }
    const isAuthenticated = authStore.isUserLoggedIn;
    const { isUserAuthorized } = authStore;

    // UNAUTHENTICATED
    if (to.meta.requiresAuth && !isAuthenticated) {
        return next({ name: RouteNameEnum.Login });
    }

    if (to.name === RouteNameEnum.Login && isAuthenticated) {
        if (authStore.isAdminErgo) {
            return next({ name: RouteNameEnum.AdminOffice });
        }
        return next({ name: RouteNameEnum.Patients });
    }

    // Check if the user is accessing a route directly or from an external link
    if (!from.name && !isUserAuthorized(to.meta.roles)) {
        // User accessed the route directly or from an external link and is not authorized
        if (authStore.isGranted(RoleEnum.ROLE_ADMIN_ERGO)) {
            return next({ name: RouteNameEnum.AdminOffice });
        }
        return next({ name: RouteNameEnum.Patients });
    }

    // UNAUTHORIZED
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((to.meta.roles as any[])?.length && !isUserAuthorized(to.meta.roles)) {
        return false;
    }
    // This branch holds the navigation without calling next(), waiting for the confirmation
    // modal. A logged out user has no order left to save, and holding him here would keep the
    // patient scan on screen: unmounting the view is what disposes the 3D scene.
    if (
        isAuthenticated &&
        ((from.name === RouteNameEnum.Scan && shouldSave.value && !navigationStore.nextToConfirm) ||
            (from.name === RouteNameEnum.SolePresetEdit && !navigationStore.nextToConfirm))
    ) {
        navigationStore.nextToConfirm = to;
        return;
    }
    navigationStore.nextToConfirm = undefined;
    if (to.name === RouteNameEnum.MyOfficeCatalog && !growthbook?.isOn('element-management')) {
        return false;
    }
    return next();
});

export default router;
